'use client'

import Image from 'next/image'
import { useEffect, useState } from 'react'

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from 'embla-carousel-react'

import { SolutionBySlugDTO } from '@/lib/models/model-types/solution/solution.types'
import { Markdown } from '@/lib/ui-kit/components/Markdown.component'
import { ButtonIcon } from '@/lib/ui-kit/components/ui/ButtonIcon.component'
import { ButtonLink } from '@/lib/ui-kit/components/ui/ButtonLink.component'
import { HeroTitle } from '@/lib/ui-kit/components/ui/HeroTitle.component'
import { IconType } from '@/lib/ui-kit/components/ui/Icon.types'

const CarouselPrevious = ({
  emblaApi,
}: {
  emblaApi: UseEmblaCarouselType[1]
}) => {
  const isDisabled = !emblaApi?.canScrollPrev()
  return (
    <ButtonIcon
      className="absolute left-[-5.5rem] top-1/2 hidden -translate-y-1/2 transform xl:flex xl:items-center xl:justify-center"
      isDisabled={isDisabled}
      onClick={() => emblaApi?.scrollPrev()}
      iconType={IconType.arrow_left}
    />
  )
}

const CarouselNext = ({ emblaApi }: { emblaApi: UseEmblaCarouselType[1] }) => {
  const isDisabled = !emblaApi?.canScrollNext()
  return (
    <ButtonIcon
      className="absolute right-[-5.5rem] top-1/2 hidden -translate-y-1/2 transform xl:flex xl:items-center xl:justify-center"
      isDisabled={isDisabled}
      onClick={() => emblaApi?.scrollNext()}
      iconType={IconType.arrow_right}
    />
  )
}

export const SectionExplore = ({
  data,
  apiAssetsUrl,
}: {
  data: SolutionBySlugDTO
  apiAssetsUrl: string | undefined
}) => {
  const { exploreTitle, exploreDescription, exploreCard } = data
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const onSelect = () => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }

  useEffect(() => {
    if (!emblaApi) return
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    onSelect()
  }, [emblaApi])

  return (
    <section className="m-auto max-w-[1440px] border-b border-gray-400 bg-gray-200 pb-6 pt-10 xl:pb-12 xl:pt-20">
      <div className="px-6 xl:px-10">
        {exploreTitle && (
          <div className="heading-xl pb-6 xl:pb-10">
            <Markdown components={{ p: HeroTitle }}>{exploreTitle}</Markdown>
          </div>
        )}
        <div className="grid grid-cols-1 xl:grid-cols-6">
          <div className="col-span-1 xl:col-span-2">
            {exploreDescription && (
              <div className="paragraph-l">
                <Markdown components={{ p: 'p' }}>
                  {exploreDescription}
                </Markdown>
              </div>
            )}
          </div>
        </div>
        {exploreCard && exploreCard.length > 0 && (
          <div className="relative mx-auto mt-10 lg:w-[85%] 2xl:w-[90%]">
            <div className="embla" ref={emblaRef}>
              <div className="embla__container flex">
                {exploreCard.map((card, index) => (
                  <div
                    className="embla__slide relative flex-[0_0_94%] md:flex-[0_0_100%]"
                    key={index}
                  >
                    <div
                      className="relative flex h-[45rem] items-center xl:h-[26rem]"
                      style={{
                        backgroundImage: `url(${apiAssetsUrl || ''}${
                          card.backgroundImage.url
                        })`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }}
                    >
                      <div className="absolute inset-0 bg-black bg-opacity-60"></div>
                      <div className="relative z-10 grid w-full grid-cols-1 p-6 xl:grid-cols-2 xl:p-36">
                        <div className="flex flex-col xl:order-2 xl:items-start xl:justify-center xl:pl-24">
                          <div className="mb-4 xl:mb-0">
                            <Image
                              src={`${apiAssetsUrl || ''}${card.logo.url}`}
                              alt={card.logo.alternativeText || 'Logo'}
                              width={140}
                              height={140}
                              className="h-auto w-auto"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col xl:order-1 xl:items-start">
                          <h1 className="mb-2 text-4xl text-white md:w-[20rem]">
                            {card.title}
                          </h1>
                          <p className="mb-8 w-full text-base text-white md:w-[20rem] xl:mb-4">
                            {card.description}
                          </p>
                          {card.CTA && (
                            <ButtonLink
                              linkUrl={card.CTA.linkUrl}
                              linkType={card.CTA.linkType}
                              buttonType={card.CTA.buttonType}
                            >
                              {card.CTA.buttonLabel}
                            </ButtonLink>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-400">
              <div className="flex justify-center">
                {scrollSnaps.map((_, index) => (
                  <div
                    key={index}
                    className={`mx-1 h-1 w-[50%] duration-300 ${
                      index === selectedIndex ? 'bg-black' : 'bg-transparent'
                    }`}
                  />
                ))}
              </div>
            </div>
            <CarouselPrevious emblaApi={emblaApi} />
            <CarouselNext emblaApi={emblaApi} />
          </div>
        )}
      </div>
    </section>
  )
}
